import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Drawer } from "../ui/Drawer/Drawer";
import { Button } from "../ui/Button/Button";
import { Badge } from "../ui/Badge/Badge";
import { Input } from "../ui/Input/Input";
import { Select } from "../ui/Select/Select";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useToast } from "../../contexts/ToastContext";
import { useAppNavigate } from "../../hooks/useAppNavigate";
import { supportApi } from "../../api/support.api";
import { useMyTickets, useCreateTicket } from "../../hooks/queries/useSupport";
import { useSupportWebSocket } from "../../hooks/useSupportWebSocket";
import * as S from "./SupportWidget.styles";

const LifebuoyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="4"></circle>
    <line x1="4.93" y1="4.93" x2="9.17" y2="9.17"></line>
    <line x1="14.83" y1="14.83" x2="19.07" y2="19.07"></line>
    <line x1="14.83" y1="9.17" x2="19.07" y2="4.93"></line>
    <line x1="4.93" y1="19.07" x2="9.17" y2="14.83"></line>
  </svg>
);

const schema = yup
  .object({
    subject: yup
      .string()
      .min(5, "Subject must be at least 5 characters")
      .max(100, "Subject is too long")
      .required("Subject is required"),
    category: yup.string().required("Category is required"),
    description: yup
      .string()
      .min(20, "Please provide more details (min 20 characters)")
      .max(5000, "Description is too long (max 5000 characters)")
      .required("Description is required"),
  })
  .required();

type SupportFormValues = yup.InferType<typeof schema>;

interface SupportWidgetProps {
  showLabel?: boolean;
}

export const SupportWidget = ({ showLabel }: SupportWidgetProps) => {
  const { t } = useTranslation(["support", "common"]);
  const { showToast } = useToast();
  const navigate = useAppNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<"list" | "create">("list");
  const [showHighlight, setShowHighlight] = useState(false);

  useEffect(() => {
    const hasSeenSupport = localStorage.getItem("hasSeenSupportWidget");
    if (!hasSeenSupport) {
      const showTimer = setTimeout(() => setShowHighlight(true), 1500);

      const hideTimer = setTimeout(() => {
        setShowHighlight(false);
        localStorage.setItem("hasSeenSupportWidget", "true");
      }, 7000); // Auto hide after 7 seconds

      return () => {
        clearTimeout(showTimer);
        clearTimeout(hideTimer);
      };
    }
  }, []);

  // Підключаємо WebSocket для оновлень у реальному часі
  useSupportWebSocket();

  // Завантажуємо список тікетів
  const { data: ticketsData, isLoading: isLoadingTickets } = useMyTickets(0);
  const { mutateAsync: createTicket } = useCreateTicket();

  // Завантажуємо глобальний лічильник непрочитаних
  const { data: unreadData } = useQuery({
    queryKey: ["supportUnreadCount"],
    queryFn: () => supportApi.getUnreadCount(),
    refetchInterval: 60000, // резервний поллінг раз на хвилину
  });

  const unreadCount = unreadData?.count || 0;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<SupportFormValues>({
    resolver: yupResolver(schema),
    defaultValues: { subject: "", category: "BUG", description: "" },
  });

  const descriptionValue = watch("description") || "";

  const onSubmit = async (data: SupportFormValues) => {
    try {
      await createTicket({
        ...data,
        environment: navigator.userAgent,
        clientRequestId: crypto.randomUUID(),
      });
      showToast(
        t("support:ticket_created_success", "Ticket created successfully!"),
        "success",
      );
      reset();
      setView("list");
    } catch (error: any) {
      showToast(
        error?.message ||
          t("support:creation_failed", "Failed to create ticket"),
        "error",
      );
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "RESOLVED":
        return (
          <Badge variant="success">
            {t("support:status_resolved", "Resolved")}
          </Badge>
        );
      case "CLOSED":
        return (
          <Badge variant="success">
            {t("support:status_closed", "Closed")}
          </Badge>
        );
      case "IN_PROGRESS":
        return (
          <Badge variant="warning">
            {t("support:status_in_progress", "In Progress")}
          </Badge>
        );
      case "OPEN":
        return (
          <Badge variant="danger">{t("support:status_open", "Open")}</Badge>
        );
      default:
        // На випадок, якщо прийде якийсь невідомий статус
        return <Badge variant="neutral">{status}</Badge>;
    }
  };

  const handleTicketOpen = (ticketId: string) => {
    setIsOpen(false);
    navigate(`/support/${ticketId}`);
  };

  const handleOpenWidget = () => {
    setIsOpen(true);
    if (showHighlight) {
      setShowHighlight(false);
      localStorage.setItem("hasSeenSupportWidget", "true");
    }
  };

  const tickets = ticketsData?.content || [];

  return (
    <div style={{ position: "relative" }}>
      <S.NavButton
        onClick={handleOpenWidget}
        aria-label={t("support:support", "Support")}
        $showLabel={showLabel}
      >
        <div style={{ position: "relative", display: "flex" }}>
          <LifebuoyIcon />
          {unreadCount > 0 && (
            <S.UnreadBadge>
              {unreadCount > 9 ? "9+" : unreadCount}
            </S.UnreadBadge>
          )}
        </div>
        {showLabel && (
          <span style={{ marginLeft: "0.5rem" }}>
            {t("support:support", "Support")}
          </span>
        )}
      </S.NavButton>

      {showHighlight && (
        <S.OnboardingTooltip>
          {t("support:need_help", "Need help? Support Center")}
        </S.OnboardingTooltip>
      )}

      <Drawer
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setTimeout(() => setView("list"), 300);
        }}
        title={
          view === "list"
            ? t("support:support_center", "Support Center")
            : t("support:create_new_ticket", "Create New Ticket")
        }
      >
        {view === "list" ? (
          <S.ViewContainer>
            {isLoadingTickets ? (
              <S.EmptyTickets>
                <p>{t("common:loading", "Loading...")}</p>
              </S.EmptyTickets>
            ) : tickets.length === 0 ? (
              <S.EmptyTickets>
                <p>
                  {t(
                    "support:no_tickets_yet",
                    "You don't have any support tickets yet.",
                  )}
                </p>
              </S.EmptyTickets>
            ) : (
              <div>
                {tickets.map((ticket) => (
                  <S.TicketCard
                    key={ticket.id}
                    type="button"
                    onClick={() => handleTicketOpen(ticket.id)}
                  >
                    <S.TicketHeader>
                      <S.TicketTitle>{ticket.subject}</S.TicketTitle>
                      {getStatusBadge(ticket.status)}
                    </S.TicketHeader>
                    <S.TicketDate>
                      #{ticket.id.slice(0, 8)} • {ticket.category} •{" "}
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </S.TicketDate>
                    <S.ViewTicketLabel>
                      <span>{t("support:view_details", "View details →")}</span>
                      {ticket.unreadCount > 0 && (
                        <Badge variant="danger">{ticket.unreadCount} new</Badge>
                      )}
                    </S.ViewTicketLabel>
                  </S.TicketCard>
                ))}
              </div>
            )}
            <S.ActionFooter>
              <Button fullWidth onClick={() => setView("create")}>
                + {t("support:new_ticket_btn", "New Support Ticket")}
              </Button>
            </S.ActionFooter>
          </S.ViewContainer>
        ) : (
          <S.SupportForm onSubmit={handleSubmit(onSubmit)}>
            <S.FormGap>
              <Select
                label={t("support:category", "Category") + " *"}
                options={[
                  {
                    value: "BUG",
                    label: t("support:cat_bug", "Bug / Technical Issue"),
                  },
                  {
                    value: "INTEGRATION",
                    label: t(
                      "support:cat_integration",
                      "Integration (FB, API, Postbacks)",
                    ),
                  },
                  {
                    value: "BILLING",
                    label: t("support:cat_billing", "Billing & Plans"),
                  },
                  {
                    value: "OTHER",
                    label: t("support:cat_other", "Other Question"),
                  },
                ]}
                error={errors.category?.message}
                {...register("category")}
              />
              <Input
                label={t("support:subject", "Subject") + " *"}
                placeholder={t(
                  "support:subject_placeholder",
                  "Brief summary of the issue",
                )}
                error={errors.subject?.message}
                {...register("subject")}
              />
              <div>
                <S.FieldLabel>
                  {t("support:description", "Description")} *
                </S.FieldLabel>
                <S.StyledTextarea
                  placeholder={t(
                    "support:desc_placeholder",
                    "Describe your issue in detail...",
                  )}
                  $hasError={!!errors.description}
                  maxLength={10000}
                  {...register("description")}
                />

                <S.TextareaFooter>
                  <div>
                    {errors.description && (
                      <S.FieldError>{errors.description.message}</S.FieldError>
                    )}
                  </div>
                  <S.CharCounter $isNearLimit={descriptionValue.length > 9500}>
                    {descriptionValue.length} / 10000
                  </S.CharCounter>
                </S.TextareaFooter>
              </div>
            </S.FormGap>

            <S.ActionFooterRow>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setView("list")}
                fullWidth
              >
                {t("common:back", "Back")}
              </Button>
              <Button type="submit" isLoading={isSubmitting} fullWidth>
                {t("support:submit_ticket", "Submit Ticket")}
              </Button>
            </S.ActionFooterRow>
          </S.SupportForm>
        )}
      </Drawer>
    </div>
  );
};
